// src/components/privacy.jsx
import React from 'react';

export default function PrivacyPolicy() {
    // Note: We removed the useEffect that locked scrolling (overflow: hidden)
    // because this is now a static part of the page, not a modal.

    return (
        <div style={styles.contentWrapper}>
            <h1 style={styles.mainTitle}>Shepherd Privacy Policy</h1>
            <p style={{ color: '#666' }}>Last updated: 16 September 2026</p>

            <div style={styles.textContent}>

                <div style={styles.section}>
                    <p>At Shepherd, we believe software should be easier to use without requiring you to give up your privacy.</p>
                    <p style={{ marginTop: '1rem' }}>Shepherd is a browser extension that helps you navigate software through interactive, step-by-step guides. Because Shepherd works directly inside the websites and applications you use, it needs access to certain information about the page you are currently working on.</p>
                    <p style={{ marginTop: '1rem' }}><strong>This policy explains exactly what that means.</strong></p>
                    <p style={{ marginTop: '1rem' }}>Our approach is simple:</p>
                    <blockquote style={styles.quote}>
                        Shepherd only accesses information needed to provide its guidance features. We don't sell your data, we don't build an advertising profile from your browsing activity, and we don't use Shepherd as a tool to monitor your browsing history.
                    </blockquote>
                    <p>Please read this policy carefully. If you have any questions about how Shepherd handles information, you can contact us at <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a>.</p>
                </div>

                {/* Section 1 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>1. Who is Shepherd?</strong>
                    <p>Shepherd is operated by The Trustee for COOSM Enterprises Trust ("Shepherd", "we", "us", or "our").</p>
                    <p style={{ marginTop: '1rem' }}>Shepherd provides a Chrome extension and related services that allow users to create, discover, and follow interactive guides for software applications.</p>
                    <p style={{ marginTop: '1rem' }}>Our website is: <a href="https://startshepherd.com/" target="_blank" rel="noreferrer">https://startshepherd.com/</a></p>
                    <p style={{ marginTop: '1rem' }}>Our privacy contact is: <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a></p>
                    <p style={{ marginTop: '1rem' }}>Where applicable, this policy is intended to explain our handling of personal information under the Australian Privacy Act 1988 and the Australian Privacy Principles, as well as other privacy laws that may apply to our users.</p>
                </div>

                {/* Section 2 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>2. The Short Version</strong>
                    <p>Before getting into the details, here's what you should know.</p>

                    <p style={styles.subheading}>Shepherd may access information on the webpage you are using</p>
                    <p>This is necessary because Shepherd needs to understand the interface you're looking at in order to show you where to click and guide you through a task.</p>
                    <p style={{ marginTop: '0.75rem' }}>For example, Shepherd may need to identify:</p>
                    <ul style={styles.list}>
                        <li>a button labelled "Create Product";</li>
                        <li>a navigation item labelled "Settings";</li>
                        <li>a form field;</li>
                        <li>a page heading;</li>
                        <li>an accessibility label;</li>
                        <li>the current application route; or</li>
                        <li>the location of an element on the page.</li>
                    </ul>

                    <p style={styles.subheading}>Shepherd does not need your browsing history</p>
                    <p>We don't need to know every website you've visited to provide our core guidance functionality.</p>

                    <p style={styles.subheading}>Shepherd does not sell your data</p>
                    <p>We do not sell personal information or browsing activity to advertisers or data brokers.</p>

                    <p style={styles.subheading}>Shepherd does not use your browsing activity for advertising profiles</p>
                    <p>The fact that you use Shopify, HubSpot, Notion, or another website with Shepherd is not used by us to build an advertising profile about you.</p>

                    <p style={styles.subheading}>Shepherd may send relevant page information to our servers</p>
                    <p>Some Shepherd features require information from the webpage to be processed by our backend services.</p>
                    <p style={{ marginTop: '0.75rem' }}>For example, when you ask Shepherd to help you accomplish something, relevant information about the current page may be sent to our servers so that Shepherd can generate or retrieve the appropriate guidance.</p>

                    <p style={styles.subheading}>Shepherd may use AI</p>
                    <p>Some features use artificial intelligence to understand software interfaces and generate guidance.</p>
                    <p style={{ marginTop: '0.75rem' }}>When this happens, relevant information required to provide the requested feature may be sent to an AI service provider.</p>

                    <p style={styles.subheading}>You are in control</p>
                    <p>You can uninstall Shepherd at any time.</p>
                    <p style={{ marginTop: '0.75rem' }}>You can also contact us to request access to, correction of, or deletion of applicable personal information we hold about you.</p>
                </div>

                {/* Section 3 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>3. What Information Does Shepherd Collect?</strong>
                    <p>The information Shepherd handles generally falls into four categories:</p>
                    <ol style={styles.list}>
                        <li>Account information</li>
                        <li>Information needed to understand webpages</li>
                        <li>Guide information</li>
                        <li>Technical and service information</li>
                    </ol>
                    <p>We explain each below.</p>
                </div>

                {/* Section 4 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>4. Account Information</strong>
                    <p>If you create a Shepherd account, we may collect information associated with that account.</p>
                    <p style={{ marginTop: '0.75rem' }}>This may include:</p>
                    <ul style={styles.list}>
                        <li>your email address;</li>
                        <li>a unique account or user identifier;</li>
                        <li>authentication information;</li>
                        <li>account creation information;</li>
                        <li>subscription status;</li>
                        <li>usage or credit information;</li>
                        <li>guides associated with your account;</li>
                        <li>account preferences; and</li>
                        <li>information you voluntarily provide when contacting us.</li>
                    </ul>
                    <p>We use this information to operate your account and provide Shepherd's features.</p>
                    <p style={{ marginTop: '0.75rem' }}>We don't require you to provide unnecessary personal information simply to use the extension.</p>
                </div>

                {/* Section 5 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>5. Information From the Websites You Use</strong>
                    <p>This is the most important part of our privacy policy because Shepherd is designed to work <strong>inside other software applications</strong>.</p>
                    <p style={{ marginTop: '0.75rem' }}>When you use a Shepherd feature on a webpage, the extension may inspect information from that webpage that is relevant to the task you are performing.</p>
                    <p style={{ marginTop: '0.75rem' }}>Depending on the website and the Shepherd feature you're using, this can include:</p>

                    <p style={styles.subheading}>Page structure</p>
                    <p>For example:</p>
                    <ul style={styles.list}>
                        <li>buttons;</li>
                        <li>links;</li>
                        <li>menus;</li>
                        <li>form fields;</li>
                        <li>headings;</li>
                        <li>element roles;</li>
                        <li>page routes; and</li>
                        <li>the relationships between elements.</li>
                    </ul>

                    <p style={styles.subheading}>Visible interface information</p>
                    <p>For example:</p>
                    <ul style={styles.list}>
                        <li>button text;</li>
                        <li>menu labels;</li>
                        <li>field labels;</li>
                        <li>headings;</li>
                        <li>navigation items; and</li>
                        <li>other text required to identify an interface element.</li>
                    </ul>

                    <p style={styles.subheading}>Accessibility information</p>
                    <p>For example:</p>
                    <ul style={styles.list}>
                        <li>ARIA labels;</li>
                        <li>accessibility roles; and</li>
                        <li>other accessibility attributes that help Shepherd reliably identify an element.</li>
                    </ul>

                    <p style={styles.subheading}>Element identification information</p>
                    <p>Shepherd may process technical information that helps it locate an element again.</p>
                    <p style={{ marginTop: '0.75rem' }}>This can include:</p>
                    <ul style={styles.list}>
                        <li>CSS selectors;</li>
                        <li>element attributes;</li>
                        <li>contextual headings;</li>
                        <li>route patterns;</li>
                        <li>element position;</li>
                        <li>element dimensions; and</li>
                        <li>other technical information required to identify an element.</li>
                    </ul>

                    <p>This information allows Shepherd to perform its core function: <strong>showing you exactly where to click.</strong></p>
                </div>

                {/* Section 6 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>6. Shepherd Does Not Need Your Entire Website</strong>
                    <p>Shepherd does not need to understand every piece of information on a webpage simply because it is technically present in the page.</p>
                    <p style={{ marginTop: '0.75rem' }}>Our intended use of webpage access is to identify and understand the interface elements necessary for the Shepherd feature you are using.</p>
                    <p style={{ marginTop: '0.75rem' }}>For example, if Shepherd needs to tell you to click a "Create" button, it may need to identify that button and its surrounding interface.</p>
                    <p style={{ marginTop: '0.75rem' }}>It does not need to build a permanent copy of the entire website.</p>
                    <p style={{ marginTop: '0.75rem' }}>We aim to follow a principle of <strong>data minimisation</strong>: only information reasonably necessary for a requested Shepherd feature should be processed.</p>
                </div>

                {/* Section 7 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>7. What About Private or Sensitive Information on a Page?</strong>
                    <p>We recognise that many of the software applications people use every day contain sensitive or confidential information.</p>
                    <p style={{ marginTop: '0.75rem' }}>For example, a business application might contain:</p>
                    <ul style={styles.list}>
                        <li>customer information;</li>
                        <li>employee information;</li>
                        <li>financial information;</li>
                        <li>business data;</li>
                        <li>private communications;</li>
                        <li>sales information; or</li>
                        <li>other confidential material.</li>
                    </ul>
                    <p><strong>Shepherd is not designed to collect this information for its own purposes.</strong></p>
                    <p style={{ marginTop: '0.75rem' }}>However, because Shepherd operates inside webpages, information contained within a webpage may be technically accessible to the extension when it is necessary to perform a requested feature.</p>
                    <p style={{ marginTop: '0.75rem' }}>For this reason, we recommend that you do not intentionally provide Shepherd with:</p>
                    <ul style={styles.list}>
                        <li>passwords;</li>
                        <li>authentication codes;</li>
                        <li>payment card numbers;</li>
                        <li>government identification numbers;</li>
                        <li>health information;</li>
                        <li>private customer records; or</li>
                        <li>other highly sensitive information</li>
                    </ul>
                    <p>unless the particular Shepherd feature specifically requires it.</p>
                    <p style={{ marginTop: '0.75rem' }}>Shepherd does not intentionally request passwords or payment card numbers as part of its normal guide functionality.</p>
                </div>

                {/* Section 8 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>8. AI and Your Information</strong>
                    <p>Some Shepherd features use artificial intelligence to help understand software interfaces and create useful guidance.</p>
                    <p style={{ marginTop: '0.75rem' }}>When you ask an AI-powered Shepherd feature for assistance, relevant information may be processed by our AI infrastructure.</p>
                    <p style={{ marginTop: '0.75rem' }}>Depending on the feature, this could include:</p>
                    <ul style={styles.list}>
                        <li>your Shepherd request;</li>
                        <li>relevant page text;</li>
                        <li>page headings;</li>
                        <li>interface labels;</li>
                        <li>element information;</li>
                        <li>software/application context;</li>
                        <li>guide information; and</li>
                        <li>technical information needed to identify webpage elements.</li>
                    </ul>
                    <p>We aim to provide the AI service with only the information reasonably necessary to perform the requested task.</p>

                    <p style={styles.subheading}>We don't use your information to train a general-purpose AI model without telling you</p>
                    <p>We do not intentionally use personal information obtained through Shepherd to train a general-purpose AI model unless we have disclosed that practice to you and have a lawful basis to do so.</p>
                    <p style={{ marginTop: '0.75rem' }}>AI service providers may independently have terms governing how their services process information. We select providers based on the requirements of the services we provide and may update providers as Shepherd evolves.</p>
                </div>

                {/* Section 9 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>9. Your Guides</strong>
                    <p>Shepherd allows people to create interactive guides that explain how to perform tasks in software.</p>
                    <p style={{ marginTop: '0.75rem' }}>When you create a guide, Shepherd may store information such as:</p>
                    <ul style={styles.list}>
                        <li>the guide name;</li>
                        <li>your instructions;</li>
                        <li>the software the guide relates to;</li>
                        <li>guide steps;</li>
                        <li>webpage element information;</li>
                        <li>selectors;</li>
                        <li>visible interface labels;</li>
                        <li>accessibility information;</li>
                        <li>route information;</li>
                        <li>expected outcomes;</li>
                        <li>technical information required to reproduce the guide; and</li>
                        <li>an identifier associated with the guide creator.</li>
                    </ul>
                    <p>This information is required to save and reproduce your guide.</p>
                </div>

                {/* Section 10 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>10. Sharing Guides</strong>
                    <p>Shepherd is designed to make software knowledge easier to share.</p>
                    <p style={{ marginTop: '0.75rem' }}>If you choose to share or publish a guide, information contained within that guide may be made available to other Shepherd users.</p>
                    <p style={{ marginTop: '0.75rem' }}>A shared guide may contain information such as:</p>
                    <ul style={styles.list}>
                        <li>its title;</li>
                        <li>instructions;</li>
                        <li>software name;</li>
                        <li>interface labels;</li>
                        <li>guide steps;</li>
                        <li>element selectors;</li>
                        <li>contextual information; and</li>
                        <li>creator information or identifier.</li>
                    </ul>
                    <p><strong>You should not put confidential information into a guide that you intend to share.</strong></p>
                    <p style={{ marginTop: '0.75rem' }}>For example, a guide should not contain:</p>
                    <ul style={styles.list}>
                        <li>customer names;</li>
                        <li>private email addresses;</li>
                        <li>passwords;</li>
                        <li>API keys;</li>
                        <li>payment information; or</li>
                        <li>confidential business information</li>
                    </ul>
                    <p>unless you specifically intend for that information to be shared.</p>
                </div>

                {/* Section 11 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>11. Why We Collect Information</strong>
                    <p>We use information for specific purposes.</p>

                    <p style={styles.subheading}>To provide Shepherd</p>
                    <p>Including:</p>
                    <ul style={styles.list}>
                        <li>displaying guides;</li>
                        <li>identifying page elements;</li>
                        <li>generating guidance;</li>
                        <li>saving guides;</li>
                        <li>retrieving guides;</li>
                        <li>managing accounts;</li>
                        <li>providing subscriptions; and</li>
                        <li>providing customer support.</li>
                    </ul>

                    <p style={styles.subheading}>To improve Shepherd</p>
                    <p>We may use limited technical and usage information to:</p>
                    <ul style={styles.list}>
                        <li>identify bugs;</li>
                        <li>understand failed guide steps;</li>
                        <li>improve guide reliability;</li>
                        <li>improve element identification;</li>
                        <li>improve performance;</li>
                        <li>understand which features are being used; and</li>
                        <li>develop new features.</li>
                    </ul>
                    <p>Where practical, we aim to use aggregated, anonymised, or minimised information for these purposes.</p>

                    <p style={styles.subheading}>To keep Shepherd secure</p>
                    <p>We may process information to:</p>
                    <ul style={styles.list}>
                        <li>detect abuse;</li>
                        <li>prevent fraud;</li>
                        <li>investigate security incidents;</li>
                        <li>protect our systems;</li>
                        <li>enforce our terms; and</li>
                        <li>protect our users.</li>
                    </ul>

                    <p style={styles.subheading}>To comply with the law</p>
                    <p>We may process or disclose information when required or permitted by applicable law.</p>
                </div>

                {/* Section 12 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>12. What We Don't Do With Your Data</strong>
                    <p>We believe this deserves its own section.</p>
                    <p style={{ marginTop: '0.75rem' }}><strong>Shepherd does not:</strong></p>
                    <ul style={styles.list}>
                        <li>sell your personal information;</li>
                        <li>sell your browsing history;</li>
                        <li>sell webpage information to advertisers;</li>
                        <li>create advertising profiles based on the websites you use;</li>
                        <li>intentionally collect your passwords;</li>
                        <li>intentionally collect your payment card numbers;</li>
                        <li>use Shepherd to maintain a record of every website you visit;</li>
                        <li>monitor your browsing activity for advertising purposes;</li>
                        <li>provide your webpage information to data brokers; or</li>
                        <li>use information obtained through Shepherd for purposes unrelated to providing, securing, or improving Shepherd unless otherwise disclosed to you.</li>
                    </ul>
                </div>

                {/* Section 13 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>13. Chrome Permissions</strong>
                    <p>Shepherd requires certain Chrome permissions because the product needs to operate within webpages.</p>
                    <p style={{ marginTop: '0.75rem' }}>We believe users should understand why these permissions exist.</p>

                    <p style={styles.subheading}>Active Tab</p>
                    <p>This allows Shepherd to work with the webpage you are currently using when you interact with the extension.</p>
                    <p style={{ marginTop: '0.75rem' }}>It helps Shepherd identify relevant page elements and provide guidance.</p>

                    <p style={styles.subheading}>Scripting</p>
                    <p>This allows Shepherd to run the code required to identify webpage elements and display interactive guidance.</p>
                    <p style={{ marginTop: '0.75rem' }}>Without this capability, Shepherd could not provide its core "show me where to click" functionality.</p>

                    <p style={styles.subheading}>Storage</p>
                    <p>This allows Shepherd to store information required for the extension to operate.</p>
                    <p style={{ marginTop: '0.75rem' }}>This may include:</p>
                    <ul style={styles.list}>
                        <li>account identifiers;</li>
                        <li>preferences;</li>
                        <li>temporary extension state; and</li>
                        <li>guide-related information.</li>
                    </ul>

                    <p style={styles.subheading}>Tabs</p>
                    <p>This allows Shepherd to obtain information about browser tabs that is necessary for the extension to understand which webpage or application is being used.</p>
                    <p style={{ marginTop: '0.75rem' }}>This permission is not used to create an advertising profile of your browsing history.</p>

                    <p style={styles.subheading}>Alarms</p>
                    <p>This permission allows Shepherd to perform certain scheduled background tasks required by the extension.</p>

                    <p style={styles.subheading}>Website Access</p>
                    <p>Shepherd requires webpage access because <strong>the webpage is where Shepherd performs its job</strong>.</p>
                    <p style={{ marginTop: '0.75rem' }}>This access allows Shepherd to identify interface elements, display guides, and provide interactive assistance.</p>
                    <p style={{ marginTop: '0.75rem' }}>We do not use this access for unrelated advertising or browsing surveillance.</p>
                </div>

                {/* Section 14 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>14. Local Storage</strong>
                    <p>Some information may be stored locally within Chrome rather than being sent to our servers.</p>
                    <p style={{ marginTop: '0.75rem' }}>This can include:</p>
                    <ul style={styles.list}>
                        <li>extension settings;</li>
                        <li>temporary state;</li>
                        <li>user/account identifiers;</li>
                        <li>cached information; and</li>
                        <li>information necessary for Shepherd to function.</li>
                    </ul>
                    <p>Local browser storage is controlled by Chrome's extension environment.</p>
                    <p style={{ marginTop: '0.75rem' }}>Uninstalling Shepherd removes the extension from your browser, although information already stored on our servers may remain until it is deleted in accordance with our retention practices.</p>
                </div>

                {/* Section 15 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>15. Information We Share</strong>
                    <p>We use selected third-party service providers to operate Shepherd.</p>
                    <p style={{ marginTop: '0.75rem' }}>These providers may provide services such as:</p>
                    <ul style={styles.list}>
                        <li>cloud infrastructure;</li>
                        <li>authentication;</li>
                        <li>database storage;</li>
                        <li>AI processing;</li>
                        <li>payment processing;</li>
                        <li>security;</li>
                        <li>communications;</li>
                        <li>analytics; and</li>
                        <li>customer support.</li>
                    </ul>
                    <p>We only share information with providers where it is reasonably necessary for them to provide their services to us or where otherwise permitted by law.</p>
                    <p style={{ marginTop: '0.75rem' }}>We may also disclose information when reasonably necessary to:</p>
                    <ul style={styles.list}>
                        <li>comply with a legal obligation;</li>
                        <li>respond to lawful legal requests;</li>
                        <li>prevent fraud or abuse;</li>
                        <li>protect the security of Shepherd;</li>
                        <li>protect the rights or safety of users or others; or</li>
                        <li>complete a corporate transaction such as a merger, acquisition, restructuring, or sale of assets.</li>
                    </ul>
                </div>

                {/* Section 16 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>16. Our Service Providers</strong>
                    <p>Shepherd may use third-party providers including:</p>

                    <p style={styles.subheading}>Firebase / Google Cloud</p>
                    <p>We may use Firebase and Google Cloud infrastructure for services such as:</p>
                    <ul style={styles.list}>
                        <li>authentication;</li>
                        <li>cloud functions;</li>
                        <li>databases;</li>
                        <li>hosting; and</li>
                        <li>other backend infrastructure.</li>
                    </ul>

                    <p style={styles.subheading}>Stripe</p>
                    <p>We may use Stripe to process paid subscriptions and payments.</p>
                    <p style={{ marginTop: '0.75rem' }}>Where Stripe processes your payment information directly, your payment information is handled according to Stripe's applicable privacy terms.</p>
                    <p style={{ marginTop: '0.75rem' }}>Shepherd does not need to store your complete payment card number to operate your subscription.</p>

                    <p style={styles.subheading}>AI Providers</p>
                    <p>Shepherd may use third-party AI services to provide AI-powered functionality.</p>
                    <p style={{ marginTop: '0.75rem' }}>The specific providers used may change over time.</p>
                    <p style={{ marginTop: '0.75rem' }}>Where this happens, relevant information required to provide the requested feature may be processed by that provider.</p>
                    <p style={{ marginTop: '0.75rem' }}>We will update this policy where material changes to our data practices require additional disclosure.</p>
                </div>

                {/* Section 17 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>17. International Processing</strong>
                    <p>Some of our service providers may operate infrastructure or personnel outside Australia.</p>
                    <p style={{ marginTop: '0.75rem' }}>This means information may be processed or stored outside Australia, including potentially in the United States or other countries where our providers operate.</p>
                    <p style={{ marginTop: '0.75rem' }}>Where applicable, we take reasonable steps to ensure that overseas handling of personal information is subject to appropriate privacy and security protections.</p>
                </div>

                {/* Section 18 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>18. How Long We Keep Information</strong>
                    <p>We don't keep personal information forever simply because we can.</p>
                    <p style={{ marginTop: '0.75rem' }}>We retain information for as long as reasonably necessary for the purpose for which it was collected, including to:</p>
                    <ul style={styles.list}>
                        <li>provide Shepherd;</li>
                        <li>maintain your account;</li>
                        <li>maintain your guides;</li>
                        <li>process payments;</li>
                        <li>provide support;</li>
                        <li>maintain security;</li>
                        <li>resolve disputes;</li>
                        <li>comply with legal obligations; and</li>
                        <li>enforce our agreements.</li>
                    </ul>
                    <p>Different information may be retained for different periods.</p>
                    <p style={{ marginTop: '0.75rem' }}>When information is no longer reasonably required, we may delete it, anonymise it, or securely dispose of it.</p>
                    <p style={{ marginTop: '0.75rem' }}>Some information may need to be retained for longer where required by law or reasonably necessary for security, accounting, fraud prevention, dispute resolution, or legal purposes.</p>
                </div>

                {/* Section 19 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>19. Security</strong>
                    <p>We take reasonable steps to protect information against:</p>
                    <ul style={styles.list}>
                        <li>unauthorised access;</li>
                        <li>unauthorised disclosure;</li>
                        <li>loss;</li>
                        <li>misuse;</li>
                        <li>alteration; and</li>
                        <li>destruction.</li>
                    </ul>
                    <p>These measures may include:</p>
                    <ul style={styles.list}>
                        <li>authentication controls;</li>
                        <li>access controls;</li>
                        <li>secure communications;</li>
                        <li>cloud security controls;</li>
                        <li>restricted access to systems; and</li>
                        <li>monitoring for security issues.</li>
                    </ul>
                    <p>However, no online service can guarantee that information will always remain completely secure.</p>
                    <p style={{ marginTop: '0.75rem' }}>You are also responsible for protecting your own devices, browser, Shepherd account, and authentication credentials.</p>
                </div>

                {/* Section 20 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>20. Your Privacy Rights</strong>
                    <p>Depending on where you live and which privacy laws apply to you, you may have rights regarding your personal information.</p>
                    <p style={{ marginTop: '0.75rem' }}>These may include the right to:</p>
                    <ul style={styles.list}>
                        <li>request access to information we hold about you;</li>
                        <li>request correction of inaccurate information;</li>
                        <li>request deletion of certain information;</li>
                        <li>ask how your information is being used;</li>
                        <li>withdraw consent where consent is the applicable legal basis;</li>
                        <li>object to certain processing; and</li>
                        <li>make a privacy complaint.</li>
                    </ul>
                    <p>To exercise applicable rights, contact us at: <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a></p>
                    <p style={{ marginTop: '0.75rem' }}>We may need to verify your identity before fulfilling certain requests.</p>
                </div>

                {/* Section 21 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>21. Deleting Your Information</strong>
                    <p>If you want to delete information associated with your Shepherd account, contact: <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a></p>
                    <p style={{ marginTop: '0.75rem' }}>Please include the email address associated with your Shepherd account and describe what you would like deleted.</p>
                    <p style={{ marginTop: '0.75rem' }}>We will take reasonable steps to delete or de-identify information that we are no longer required or permitted to retain.</p>
                    <p style={{ marginTop: '0.75rem' }}>Some information may need to remain available where we are legally required to keep it or where it is reasonably necessary for:</p>
                    <ul style={styles.list}>
                        <li>legal claims;</li>
                        <li>fraud prevention;</li>
                        <li>security;</li>
                        <li>accounting;</li>
                        <li>dispute resolution; or</li>
                        <li>compliance with legal obligations.</li>
                    </ul>
                    <p>Deleting your account may also delete or make unavailable guides, account information, credits, subscription information, and other content associated with that account.</p>
                </div>

                {/* Section 22 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>22. Uninstalling Shepherd</strong>
                    <p>You can uninstall Shepherd from Chrome whenever you choose.</p>
                    <p style={{ marginTop: '0.75rem' }}>Uninstalling the extension stops the extension from operating in your browser.</p>
                    <p style={{ marginTop: '0.75rem' }}>However, uninstalling the extension does not automatically mean that all information previously associated with your Shepherd account is immediately deleted from our servers.</p>
                    <p style={{ marginTop: '0.75rem' }}>If you want your account information deleted, please contact us using the process described above.</p>
                </div>

                {/* Section 23 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>23. Children's Privacy</strong>
                    <p>Shepherd is not directed specifically at children under 13.</p>
                    <p style={{ marginTop: '0.75rem' }}>We do not knowingly create accounts for children under 13 or intentionally collect their personal information.</p>
                    <p style={{ marginTop: '0.75rem' }}>If you believe a child has provided personal information to Shepherd, please contact us at <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a>.</p>
                    <p style={{ marginTop: '0.75rem' }}>We will take reasonable steps to investigate and delete information where appropriate.</p>
                </div>

                {/* Section 24 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>24. Third-Party Websites and Software</strong>
                    <p>Shepherd can operate within third-party software applications and websites.</p>
                    <p style={{ marginTop: '0.75rem' }}>Those services are controlled by their respective operators.</p>
                    <p style={{ marginTop: '0.75rem' }}>For example, if you use Shepherd while logged into another software platform, that platform remains responsible for its own collection and use of information.</p>
                    <p style={{ marginTop: '0.75rem' }}><strong>This Privacy Policy applies to Shepherd — not to the third-party software you use with Shepherd.</strong></p>
                    <p style={{ marginTop: '0.75rem' }}>We recommend reviewing the privacy policy of any third-party service containing information that you consider sensitive or confidential.</p>
                </div>

                {/* Section 25 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>25. Changes to This Policy</strong>
                    <p>As Shepherd develops, our features, technology, and service providers may change.</p>
                    <p style={{ marginTop: '0.75rem' }}>We may therefore update this Privacy Policy from time to time.</p>
                    <p style={{ marginTop: '0.75rem' }}>When we make changes, we will update the "Last updated" date at the top of this policy.</p>
                    <p style={{ marginTop: '0.75rem' }}>If we make a material change to the way we handle personal information, we may provide additional notice where appropriate.</p>
                </div>

                {/* Section 26 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>26. Contact Us</strong>
                    <p>If you have a question about privacy, your information, or how Shepherd works, we'd rather you ask us.</p>
                    <p style={{ marginTop: '0.75rem' }}>You can contact us at:</p>
                    <p style={{ marginTop: '0.75rem' }}>
                        <strong>Shepherd</strong><br />
                        <strong>Operated by:</strong> The Trustee for COOSM Enterprises Trust<br /><br />
                        <strong>Privacy:</strong> <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a><br />
                        <strong>Website:</strong> <a href="https://startshepherd.com/" target="_blank" rel="noreferrer">https://startshepherd.com/</a><br /><br />
                        <strong>Australia</strong>
                    </p>
                </div>

                {/* Section 27 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>27. Privacy Questions — Answered</strong>

                    <p style={styles.subheading}>Does Shepherd see everything I do online?</p>
                    <p><strong>No.</strong> Shepherd's core functionality requires access to webpages so that it can identify interface elements and provide guidance, but Shepherd is not designed to maintain a record of every website you visit or monitor your browsing for advertising.</p>

                    <p style={styles.subheading}>Can Shepherd see my passwords?</p>
                    <p>Shepherd does not intentionally collect passwords. However, because the extension operates within webpages, you should avoid intentionally exposing passwords or other highly sensitive information to Shepherd.</p>

                    <p style={styles.subheading}>Does Shepherd sell my data?</p>
                    <p><strong>No.</strong> We do not sell your personal information, webpage information, or browsing history to advertisers or data brokers.</p>

                    <p style={styles.subheading}>Does Shepherd track me for advertising?</p>
                    <p><strong>No.</strong> Shepherd does not use the websites you use with the extension to create an advertising profile about you.</p>

                    <p style={styles.subheading}>Does Shepherd send webpage information to AI?</p>
                    <p>Some AI-powered features may do so. When you request those features, relevant information needed to generate guidance may be sent to our AI infrastructure.</p>

                    <p style={styles.subheading}>Do you use my information to train AI?</p>
                    <p>We do not intentionally use personal information obtained through Shepherd to train a general-purpose AI model unless that practice is disclosed to you and permitted by applicable law.</p>

                    <p style={styles.subheading}>Can I delete my data?</p>
                    <p>You can contact us at <a href="mailto:luke@startshepherd.com">luke@startshepherd.com</a> to request deletion of applicable personal information.</p>

                    <p style={styles.subheading}>Why does Shepherd need website access?</p>
                    <p>Because Shepherd's job is to guide you <strong>inside the software you're using</strong>. Website access allows it to identify things such as buttons, menus, fields, headings, and other interface elements so it can show you where to click.</p>
                </div>

                {/* Section 28 */}
                <div style={styles.section}>
                    <strong style={styles.heading}>28. Our Privacy Commitment</strong>
                    <p>Shepherd exists to make software easier to understand.</p>
                    <p style={{ marginTop: '0.75rem' }}>We don't believe that making software easier should mean making your personal information less private.</p>
                    <p style={{ marginTop: '0.75rem' }}>Our goal is to collect and process only the information reasonably necessary to provide Shepherd's features, protect the service, and improve the product.</p>
                    <p style={{ marginTop: '0.75rem' }}><strong>Your software is your business. Your data is your data.</strong></p>
                    <p style={{ marginTop: '0.75rem' }}>We will continue to build Shepherd with that principle in mind.</p>
                    <p style={{ marginTop: '1.5rem', color: '#666' }}>Last updated: 16 September 2026</p>
                </div>
            </div>
        </div>
    );
}

const styles = {
    contentWrapper: {
        color: "black",
        textAlign: "left",
        // We removed the fixed position, backdrop blur, and fixed heights
    },
    textContent: {
        marginTop: "1.5rem",
    },
    mainTitle: {
        fontSize: "2rem",
        marginBottom: "0.5rem",
        marginTop: 0,
        color: '#1a73e8'
    },
    section: {
        marginBottom: "2rem",
        lineHeight: "1.6",
        color: "#333"
    },
    heading: {
        display: "block",
        marginBottom: "0.5rem",
        fontSize: "1.2rem",
        color: "#000"
    },
    subheading: {
        fontWeight: 600,
        marginTop: "1.25rem",
        marginBottom: "0.35rem",
        color: "#000"
    },
    list: {
        margin: "0.5rem 0 1rem 1.25rem",
        padding: 0,
    },
    quote: {
        margin: "1rem 0",
        padding: "1rem 1.25rem",
        borderLeft: "4px solid #1a73e8",
        background: "#f0f5ff",
        borderRadius: "4px",
        color: "#1a1f36",
        fontStyle: "italic",
    },
};
